import { format } from "date-fns";
import parseDateYmd from "lib/utils/parseDateYmd";
import type { ReactElement } from "react";
import * as React from "react";
import type { IMonthlyBrazilianRealEstateFundFinancialStatementRow } from "../../types/graphqlTypes";
import FinancialValueThresholdColumnStyles from "../financials/FinancialValueThresholdColumnStyles";
import formatFinancialValue from "../financials/formatFinancialValue";
import * as palette from "../palette";
import TextTooltip from "../shared/TextTooltip";

interface IRealEstateFundFinancialsProps {
  financials: IMonthlyBrazilianRealEstateFundFinancialStatementRow[];
  displayAllColumns?: boolean;
}

export default function RealEstateFundFinancials({
  financials,
  displayAllColumns = false,
  ...props
}: IRealEstateFundFinancialsProps & React.TableHTMLAttributes<HTMLTableElement>): ReactElement | null {
  if (financials.length === 0) {
    return null;
  }

  return (
    <table
      css={{
        borderRadius: 4,
        borderSpacing: 0,
        borderCollapse: "collapse",
        "> thead > tr > th:first-of-type": {
          borderTopLeftRadius: 4,
        },
        "> thead > tr > th:last-of-type": {
          borderTopRightRadius: 4,
        },
        "> tbody > tr:last-of-type > td:first-of-type": {
          borderBottomLeftRadius: 4,
        },
        "> tbody > tr:last-of-type > td:last-of-type": {
          borderBottomRightRadius: 4,
        },
        "> thead > tr > th": {
          position: "sticky",
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
        "& > tbody > tr > td": {
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
      }}
      {...props}
    >
      <thead>
        <tr>
          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.white,
              fontWeight: 600,
              backgroundColor: palette.blueBayoux,
              borderRight: `solid 1px ${palette.lynch}`,
            }}
          >
            Ano
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.lynch}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/capitalWithLiquidity" placement="top" content="Capital com Liquidez">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Caixa
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/totalValueOfRealEstate" placement="top" content="Valor Total dos Imóveis">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Imóveis
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/totalOfFundsOfFunds" placement="top" content="Investimentos em FIIs">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Fundos
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/totalOfOtherInvestments"
              placement="top"
              content="Investimentos em CRIs, LCIs e afins"
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Papéis
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/totalFromRents" placement="top" content="Recursos Provenientes de Aluguéis">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Aluguéis
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/totalFromOthers"
              placement="top"
              content="Aquisições, Adiantamentos e Outros Custos"
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Outros
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/totalAssets" placement="top" content="Ativos Totais">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Ativos
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              fontWeight: 600,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/distributedIncome"
              placement="top"
              content="Rendimentos Distribuídos aos Cotistas"
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Rendim.
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              fontWeight: 600,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/performanceAndAdministrationTaxes"
              placement="top"
              content="Custos com Taxas de Administração e Performance"
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Taxas
                </span>
              )}
            </TextTooltip>
          </th>

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.mojo,
                fontWeight: 600,
                backgroundColor: "#faecec",
                borderLeft: `solid 1px #edd1d1`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip id="tooltips/th/taxesToDistributedIncomeRatio" placement="top" content="Taxas/Rendimentos">
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    % Taxas
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              fontWeight: 600,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/otherCosts"
              placement="top"
              content="Aquisições, Adiantamentos e Outros Custos"
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Outros
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              fontWeight: 600,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/totalLiabilities" placement="top" content="Passivos Totais">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Passivos
                </span>
              )}
            </TextTooltip>
          </th>

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.mojo,
                fontWeight: 600,
                backgroundColor: "#faecec",
                borderLeft: `solid 1px #edd1d1`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip
                id="tooltips/th/leverageToTotalAssets"
                placement="top"
                content="Obrigações de Imóveis/Total de Ativos"
              >
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    Alavancagem
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.royalBlue,
              fontWeight: 600,
              backgroundColor: palette.selago,
              borderLeft: `solid 1px ${palette.tropicalBlue}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/netEquity" placement="top" content="Patrimônio Líquido">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Pat. Líq.
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.scienceBlue,
              fontWeight: 600,
              backgroundColor: palette.lilyWhite,
              borderLeft: `solid 1px ${palette.frenchPass}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/netTotalFinancialFlow"
              placement="top"
              content="Fluxo Financeiro Total Descontado dos Rendimentos Distribuídos (*Valor Trimestral*)"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  F. Total Líq.
                </span>
              )}
            </TextTooltip>
          </th>

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.mineShaft,
                fontWeight: 600,
                backgroundColor: palette.catskillWhite,
                borderLeft: `solid 1px ${palette.mystic}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip id="tooltips/th/equityValuePerQuota" placement="top" content="Valor Patrimonial por Cota">
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    V.P.C.
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.luxorGold,
              fontWeight: 600,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/yieldPerQuota" placement="top" content="Rendimentos por Cota">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Rend.
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.luxorGold,
              fontWeight: 600,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/dividendYield"
              placement="top"
              content="Dividend Yield em Relação ao V.P.C do mês anterior"
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  DY
                </span>
              )}
            </TextTooltip>
          </th>
        </tr>
      </thead>

      <tbody
        css={{
          "& > tr > td": {
            textAlign: "center",
          },
          "& > tr > td:not(:first-of-type)": {
            borderLeft: `solid 1px ${palette.mystic}`,
            borderRight: `solid 1px ${palette.mystic}`,
            borderBottom: `solid 1px ${palette.mystic}`,
          },
          "& > tr:hover": {
            backgroundColor: "rgb(241, 244, 248, 0.5)",
          },
          "& > tr:last-of-type > td": {
            borderBottom: "none",
          },
        }}
      >
        {financials.map((financialStatementRow) => {
          return (
            <tr key={financialStatementRow.fiscalDate}>
              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  backgroundColor: palette.blueBayoux,
                  fontWeight: 600,
                  color: palette.white,
                  lineHeight: "20px",
                  borderTop: `solid 1px ${palette.lynch}`,
                  borderBottom: `solid 1px ${palette.lynch}`,
                  borderRight: `solid 1px ${palette.lynch}`,
                }}
              >
                {format(parseDateYmd(financialStatementRow.fiscalDate), "MM/yyyy")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.seaGreen,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.capitalWithLiquidity, "unit")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.seaGreen,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.totalValueOfRealEstate, "unit")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.seaGreen,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.totalOfFundsOfFunds, "unit")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.seaGreen,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.totalOfOtherInvestments, "unit")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.seaGreen,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.totalFromRents, "unit")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.seaGreen,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.totalFromOthers, "unit")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.seaGreen,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                  backgroundColor: "#F1FFF3",
                }}
              >
                {Math.round(financialStatementRow.totalAssets / 1000 / 1000)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.mojo,
                  lineHeight: "20px",
                  border: `solid 1px #eec9c9`,
                }}
              >
                {formatFinancialValue(financialStatementRow.distributedIncome, "unit")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.mojo,
                  lineHeight: "20px",
                  border: `solid 1px #eec9c9`,
                }}
              >
                {formatFinancialValue(financialStatementRow.performanceAndAdministrationTaxes, "unit")}
              </td>

              {displayAllColumns && (
                <FinancialValueThresholdColumnStyles
                  value={
                    financialStatementRow.distributedIncome === 0
                      ? 0
                      : (financialStatementRow.performanceAndAdministrationTaxes /
                          financialStatementRow.distributedIncome) *
                        100
                  }
                  comparators={[
                    (value) => value === 0,
                    (value) => value <= 10 && value > 0,
                    (value) => value > 10 && value < 15,
                    (value) => value > 15,
                  ]}
                  styles={[
                    () => ({}),
                    () => ({
                      color: palette.seaGreen,
                      backgroundColor: "#F1FFF3",
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                    () => ({
                      color: palette.kumera,
                      backgroundColor: palette.butteryWhite,
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),

                    () => ({
                      color: palette.mojo,
                      backgroundColor: "#FCF1F1",
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                  ]}
                >
                  {(taxesToDistributedIncomeRatio, css) => (
                    <td
                      css={{
                        fontFamily: "novel-mono-pro-condensed",
                        fontWeight: 600,
                        color: palette.mojo,
                        lineHeight: 1.33,
                        border: `solid 1px #eec9c9`,
                        ...css,
                      }}
                    >
                      {`${taxesToDistributedIncomeRatio
                        ?.toFixed(taxesToDistributedIncomeRatio === 0 ? 0 : 1)
                        .replace(".", ",")}%`}
                    </td>
                  )}
                </FinancialValueThresholdColumnStyles>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.mojo,
                  lineHeight: "20px",
                  border: `solid 1px #eec9c9`,
                }}
              >
                {formatFinancialValue(financialStatementRow.otherCosts, "unit")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.mojo,
                  lineHeight: "20px",
                  border: `solid 1px #eec9c9`,
                  borderRight: "none",
                  backgroundColor: "#FCF1F1",
                }}
              >
                {Math.round(financialStatementRow.totalLiabilities / 1000 / 1000)}
              </td>

              {displayAllColumns && (
                <FinancialValueThresholdColumnStyles
                  value={
                    financialStatementRow.totalAssets === 0
                      ? 0
                      : (financialStatementRow.leverage / financialStatementRow.totalAssets) * 100
                  }
                  comparators={[
                    (value) => {
                      return value === 0;
                    },
                    (value) => {
                      return value <= 30 && value > 0;
                    },
                    (value) => {
                      return value > 30 && value < 60;
                    },
                    (value) => {
                      return value >= 60;
                    },
                  ]}
                  styles={[
                    () => ({}),
                    () => ({
                      color: palette.seaGreen,
                      backgroundColor: "#F1FFF3",
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                    () => ({
                      color: palette.kumera,
                      backgroundColor: palette.butteryWhite,
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),

                    () => ({
                      color: palette.mojo,
                      backgroundColor: "#FCF1F1",
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                  ]}
                >
                  {(leverageToTotalAssets, css) => (
                    <td
                      css={{
                        fontFamily: "novel-mono-pro-condensed",
                        fontWeight: 600,
                        color: palette.mojo,
                        lineHeight: 1.33,
                        border: `solid 1px #eec9c9`,
                        ...css,
                      }}
                    >
                      {`${leverageToTotalAssets?.toFixed(leverageToTotalAssets === 0 ? 0 : 1).replace(".", ",")}%`}
                    </td>
                  )}
                </FinancialValueThresholdColumnStyles>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.royalBlue,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.tropicalBlue}`,
                  backgroundColor: "#F4F6FF",
                }}
              >
                {Math.round(financialStatementRow.netEquity / 1000 / 1000)}
              </td>

              <FinancialValueThresholdColumnStyles
                value={financialStatementRow.netTotalFinancialFlow}
                comparators={[(value) => value < 0, (value) => value > 0]}
                styles={[
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                ]}
              >
                {(netTotalFinancialFlow, css) => (
                  <td
                    css={{
                      border: `solid 1px ${palette.frenchPass}`,
                      fontFamily: "novel-mono-pro-condensed",
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: "20px",
                      ...css,
                    }}
                  >
                    {netTotalFinancialFlow === 0 ? "-" : formatFinancialValue(netTotalFinancialFlow, "unit")}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>

              {displayAllColumns && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight: 600,
                    color: palette.blueBayoux,
                    lineHeight: "20px",
                    border: `solid 1px ${palette.mystic}`,
                  }}
                >
                  {financialStatementRow.equityValuePerQuota.toFixed(1).replace(".", ",")}
                </td>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.luxorGold,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.chamois}`,
                }}
              >
                {financialStatementRow.yieldPerQuota === 0
                  ? "0"
                  : financialStatementRow.yieldPerQuota.toFixed(2).replace(".", ",")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 500,
                  color: palette.luxorGold,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.chamois}`,
                }}
              >
                {financialStatementRow.dividendYield === 0
                  ? "0"
                  : financialStatementRow.dividendYield.toFixed(2).replace(".", ",")}
                %
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
